import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Radio, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '../components/ui/input';
import api from '../api/client';
import { discoverLives } from '../api/lives';
import PersonCard from '../components/PersonCard';
import Avatar from '../components/Avatar';
import { useCategories } from '../api/categories';
import { recordMediaEvent, setTelemetryPage } from '../lib/mediaTelemetry';

/**
 * ExplorePage → "Ricerca" — Voice-First PEOPLE discovery (no content grid).
 * REUSES ONLY existing systems:
 *  - GET /api/users/search (username search, zero PII) for the search box;
 *  - GET /api/feed/people (+ existing category filter) for discovery;
 *  - the EXISTING PersonCard (no second user card);
 *  - LIVE ORA strip + private profiles strip (people, avatar only).
 * No photo/video/post tiles. Route stays /explore.
 */

const PAGE = 20;

// Map the /users/search UserPublic shape onto the PersonCard shape used by
// /feed/people — same underlying users collection, no invented data.
const mapSearchUser = (u) => ({
  user_id: u.id,
  username: u.username,
  avatar_url: u.avatar_url || '',
  bio: u.bio || '',
  languages: u.languages || [],
  topics: u.topics || [],
  category_slug: u.category_slug || null,
  gender: u.gender || null,
  audio_avatar_url: u.audio_avatar_url || '',
  is_voce_pro: !!u.is_verified_creator,
  is_private_profile: !!u.is_private,
  is_online: !!u.is_online,
  is_live: false,
  is_favorite: !!u.is_following,
  o2o_enabled: !!u.o2o_mode,
  o2o_available: u.o2o_state === 'available',
  o2o_rate_credits_per_min: u.o2o_rate_credits_per_min || 0,
});

const ExplorePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('explore');
  const { categories } = useCategories();
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState((searchParams.get('category') || '').toLowerCase());
  const [people, setPeople] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [liveNow, setLiveNow] = useState([]);
  const [privateCreators, setPrivateCreators] = useState([]);
  const [q, setQ] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  const loadPeople = async (cat, skip = 0, append = false) => {
    if (append) setLoadingMore(true); else setLoadingPeople(true);
    try {
      const params = { limit: PAGE, skip };
      if (cat) params.category = cat;
      const r = await api.get('/feed/people', { params });
      const items = r.data?.people || [];
      setPeople((prev) => (append ? [...prev, ...items] : items));
      setHasMore(!!r.data?.has_more);
    } catch (_e) {
      if (!append) setPeople([]);
      setHasMore(false);
    } finally {
      setLoadingPeople(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadPeople(category, 0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    discoverLives()
      .then((r) => setLiveNow(r.live_now || []))
      .catch(() => setLiveNow([]));
    api.get('/explore/private-creators')
      .then((r) => setPrivateCreators(r.data || []))
      .catch(() => setPrivateCreators([]));
    recordMediaEvent('explore_views');
    setTelemetryPage('explore');
    return () => setTelemetryPage(null);
  }, []);

  // Debounced people search — EXISTING /users/search endpoint (username only).
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const cleaned = (q || '').trim().replace(/^@/, '');
    if (cleaned.length < 2) {
      setUserResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get('/users/search', { params: { q: cleaned, limit: 20 } });
        setUserResults(Array.isArray(r.data) ? r.data.map(mapSearchUser) : []);
      } catch (_e) {
        setUserResults([]);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [q]);

  const isSearchMode = q.trim().replace(/^@/, '').length >= 2;

  return (
    <div className="mx-auto max-w-[935px] px-1 pb-24 pt-4 md:px-4" data-testid="explore-page">
      <div className="mb-3 px-3">
        <h1 className="text-xl font-bold" data-testid="explore-page-title">{t('page_title')}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">{t('page_subtitle')}</p>
      </div>

      <div className="mb-4 px-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('search.placeholder')}
            className="h-10 rounded-md bg-secondary pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="explore-search-input"
          />
        </div>
      </div>

      {/* Search mode — PersonCard results from /users/search */}
      {isSearchMode ? (
        <div className="px-3" data-testid="explore-user-results">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-500">
            <Search className="h-3.5 w-3.5" /> {t('people.results')}
          </p>
          {searching ? (
            <p className="text-xs text-muted-foreground">{t('search.searching')}</p>
          ) : userResults.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="explore-user-empty">
              {t('search.no_users', { query: q })}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {userResults.map((u) => <PersonCard key={u.user_id} p={u} />)}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* LIVE ORA — existing Live system (people, no post imagery) */}
          {liveNow.length > 0 && (
            <div className="mb-4 px-3" data-testid="explore-live-strip">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-500">
                <Radio className="h-3.5 w-3.5" /> {t('live_strip.title')}
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {liveNow.slice(0, 12).map((live) => (
                  <button
                    key={live.id}
                    onClick={() => navigate(`/lives/${live.id}`)}
                    className="flex w-40 shrink-0 flex-col items-start rounded-lg border border-red-500/30 bg-gradient-to-br from-red-900/20 via-neutral-900 to-rose-900/20 p-2 text-left hover:border-red-400"
                    data-testid="explore-live-card"
                  >
                    <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                      LIVE
                    </span>
                    <p className="mt-1 line-clamp-2 text-xs font-semibold">{live.title}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">@{live.creator?.username}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Private profiles strip — people only, common Avatar */}
          {privateCreators.length > 0 && (
            <div className="mb-4 px-3" data-testid="explore-private-strip">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-500">
                <Lock className="h-3.5 w-3.5" /> {t('private_strip.title')}
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {privateCreators.slice(0, 12).map((c) => (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/u/${c.username}`)}
                    className="flex w-44 shrink-0 flex-col items-start rounded-lg border border-amber-500/30 bg-gradient-to-br from-amber-900/15 via-neutral-900 to-neutral-900 p-2.5 text-left hover:border-amber-400"
                    data-testid="explore-private-card"
                  >
                    <div className="flex w-full items-center gap-2">
                      <Avatar src={c.avatar_url} name={c.username} size={32} />
                      <p className="flex min-w-0 items-center gap-1 text-xs font-semibold">
                        <span className="truncate">@{c.username}</span>
                        <Lock className="h-3 w-3 shrink-0 text-amber-400" />
                      </p>
                    </div>
                    <p className="mt-1.5 text-[10px] text-muted-foreground">
                      {c.price_credits > 0
                        ? t('private_strip.price', { amount: c.price_credits })
                        : t('private_strip.free')}
                    </p>
                    <span
                      className={`mt-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        c.my_status === 'approved'
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : c.my_status === 'pending'
                            ? 'bg-white/10 text-neutral-300'
                            : 'gold-gradient text-black'
                      }`}
                      data-testid="explore-private-cta"
                    >
                      {c.my_status === 'approved'
                        ? t('private_strip.member')
                        : c.my_status === 'pending'
                          ? t('private_strip.pending')
                          : t('private_strip.request_access')}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Category chips — EXISTING admin taxonomy ("Tutti" + categories) */}
          <div className="mb-4 px-3">
            <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="explore-category-chips">
              <button
                onClick={() => setCategory('')}
                className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-bold ${
                  !category ? 'border-amber-500 bg-amber-500/15 gold-text' : 'border-border text-muted-foreground hover:bg-secondary'
                }`}
                data-testid="explore-category-chip-all"
              >
                {t('people.all')}
              </button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setCategory(category === c.slug ? '' : c.slug)}
                  className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold ${
                    category === c.slug ? 'border-amber-500 bg-amber-500/15 gold-text' : 'border-border text-muted-foreground hover:bg-secondary'
                  }`}
                  data-testid={`explore-category-chip-${c.slug}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* People — EXISTING PersonCard + /feed/people */}
          <div className="px-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('people.header')}
            </p>
            {loadingPeople ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="explore-people-skeleton">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-36 animate-pulse rounded-xl border border-border bg-secondary/40" />
                ))}
              </div>
            ) : people.length === 0 ? (
              <p className="rounded-xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground" data-testid="explore-people-empty">
                {t('people.none')}
              </p>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="explore-people-grid">
                  {people.map((p) => <PersonCard key={p.user_id} p={p} />)}
                </div>
                {hasMore && (
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={() => loadPeople(category, people.length, true)}
                      disabled={loadingMore}
                      className="rounded-full border border-border px-5 py-2 text-sm font-semibold text-muted-foreground hover:bg-secondary disabled:opacity-50"
                      data-testid="explore-load-more"
                    >
                      {loadingMore ? t('people.loading') : t('people.load_more')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ExplorePage;
